import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  Button,
  AppBar,
  Toolbar,
  CssBaseline,
  ThemeProvider,
  Grid,
  Card,
  CardContent,
  CardActions,
  Chip,
  CircularProgress
} from '@mui/material';
import { theme } from '../theme/theme';
import logo from '../PuerilAlgritmo_Icon.png';
import { useNavigate } from 'react-router-dom';
import { getPlans, PlanType } from '../services/subcriptions';

// Interfaz para mostrar los planes en la UI
interface DisplayPlan {
  title: string;
  price: string;
  description: string;
  features: string[];
  buttonText: string;
  chipLabel?: string;
  id: number;
  billing_cycle: string;
}

const Pricing: React.FC = () => {
  const navigate = useNavigate();
  const [plans, setPlans] = useState<DisplayPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        setLoading(true);
        const apiPlans = await getPlans();
        
        // Transformar los planes de la API al formato de la UI
        const formattedPlans = apiPlans.map((plan: PlanType) => {
          // Convertir la descripción en un array de características
          const featuresArray = plan.description.split('\n').filter(item => item.trim() !== '');
          
          return {
            id: plan.id,
            title: plan.name,
            price: plan.billing_cycle === 'monthly' 
              ? `$${plan.price}/mes`
              : plan.billing_cycle === 'weekly'
              ? `$${plan.price}/semana`
              : `$${plan.price}/año`,
            description: plan.description.substring(0, 0) + '...',
            features: featuresArray,
            buttonText: 'Seleccionar Plan',
            billing_cycle: plan.billing_cycle,
            // Opcionalmente añadir chipLabel si es un plan destacado
            chipLabel: plan.name==='Premium' ? 'Popular' : undefined
          };
        });
        
        setPlans(formattedPlans);
        setLoading(false);
      } catch (err) {
        console.error('Error al cargar los planes:', err);
        setError('No se pudieron cargar los planes. Por favor, inténtalo de nuevo más tarde.');
        setLoading(false);
      }
    };

    fetchPlans();
  }, []);

  // Filtrar planes para mostrar principales y de prueba
  const mainPlans = plans.filter(plan => !plan.title.toLowerCase().includes('freemium') && !plan.title.toLowerCase().includes('prueba'));
  const freemiumPlans = plans.filter(plan => plan.title.toLowerCase().includes('freemium') || plan.title.toLowerCase().includes('prueba'));

  const handleSelectPlan = (planId: number) => {
    // Aquí puedes implementar la lógica para seleccionar un plan
    // Por ejemplo, redirigir a una página de pago con el ID del plan
    navigate(`/payment/${planId}`);
  };

  if (loading) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          minHeight: '100vh',
          background: theme.palette.background.default
        }}>
          <CircularProgress color="primary" />
        </Box>
      </ThemeProvider>
    );
  }

  if (error) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          minHeight: '100vh',
          background: theme.palette.background.default,
          color: theme.palette.common.white
        }}>
          <Typography variant="h6">{error}</Typography>
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ background: theme.palette.background.default, minHeight: '100vh' }}>
        <AppBar position="static" sx={{ backgroundColor: 'rgba(255, 255, 255, 0.9)', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}>
          <Toolbar sx={{ justifyContent: 'space-between', paddingY: { xs: 1, md: 2 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={() => navigate('/')}>
              <img src={logo} width={86} alt="Logo" />
              <Typography variant="h6" component="div" sx={{ color: theme.palette.common.black, fontWeight: 'bold', fontSize: { xs: '1.2rem', md: '1.5rem' } }}>
                Pueril Algoritmo
              </Typography>
            </Box>
            <Box>
              <Button
                variant="text"
                sx={{
                  color: theme.palette.primary.main,
                  '&:hover': {
                    backgroundColor: 'transparent',
                    textDecoration: 'underline',
                  },
                }}
                onClick={() => navigate('/login')}
              >
                Iniciar Sesión
              </Button>
              <Button
                variant="outlined"
                sx={{
                  color: theme.palette.primary.main,
                  borderColor: theme.palette.primary.main,
                  marginLeft: 2,
                  '&:hover': {
                    backgroundColor: theme.palette.primary.light,
                    color: 'white',
                  },
                }}
                onClick={() => navigate('/register')}
              >
                Registrarse
              </Button>
            </Box>
          </Toolbar>
        </AppBar>

        <Container maxWidth="lg" sx={{ paddingY: { xs: 8, md: 10 } }}>
          <Typography
            variant="h3"
            component="h2"
            sx={{
              textAlign: 'center',
              fontWeight: 'bold',
              color: theme.palette.common.white,
              mb: 2,
            }}
          >
            Planes de Precios
          </Typography>
          <Typography
            variant="h6"
            component="p"
            sx={{
              textAlign: 'center',
              color: theme.palette.common.white,
              mb: 8,
            }}
          >
            Elige el plan que mejor se adapte a tus necesidades.
          </Typography>
          
          {mainPlans.length > 0 ? (
            <Grid container spacing={4} justifyContent="center">
              {mainPlans.map((plan) => (
                <Grid item key={plan.id} xs={12} sm={6} md={4}>
                  <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', borderRadius: '1rem', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)' }}>
                    <CardContent sx={{ flexGrow: 1, p: 4 }}>
                      {plan.chipLabel && <Chip label={plan.chipLabel} color="primary" sx={{ mb: 2 }} />}
                      <Typography variant="h5" component="h3" sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mb: 2 }}>
                        {plan.title}
                      </Typography>
                      <Typography variant="h4" component="p" sx={{ color: theme.palette.primary.main, mb: 2 }}>
                        {plan.price}
                      </Typography>
                      <Typography variant="body1" sx={{ color: theme.palette.text.secondary, mb: 3 }}>
                        {plan.description}
                      </Typography>
                      <Box>
                        {plan.features.map((feature, index) => (
                          <Typography key={index} variant="body2" sx={{ color: theme.palette.text.secondary, mb: 1 }}>
                            ✓ {feature}
                          </Typography>
                        ))}
                      </Box>
                    </CardContent>
                    <CardActions sx={{ p: 4, pt: 0 }}>
                      <Button
                        fullWidth
                        variant="contained"
                        color="primary"
                        sx={{
                          fontWeight: 'bold',
                          padding: '0.75rem 1.5rem',
                        }}
                        onClick={() => handleSelectPlan(plan.id)}
                      >
                        {plan.buttonText}
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          ) : (
            <Typography
              variant="h6"
              sx={{
                textAlign: 'center',
                color: theme.palette.common.white,
                my: 4,
              }}
            >
              No hay planes principales disponibles en este momento.
            </Typography>
          )}

          {freemiumPlans.length > 0 && (
            <>
              <Typography
                variant="h3"
                component="h2"
                sx={{
                  textAlign: 'center',
                  fontWeight: 'bold',
                  color: theme.palette.common.white,
                  mt: 8,
                  mb: 2,
                }}
              >
                Planes de Prueba
              </Typography>
              <Typography
                variant="h6"
                component="p"
                sx={{
                  textAlign: 'center',
                  color: theme.palette.common.white,
                  mb: 8,
                }}
              >
                Experimenta nuestras funcionalidades avanzadas antes de suscribirte.
              </Typography>
              <Grid container spacing={4} justifyContent="center">
                {freemiumPlans.map((plan) => (
                  <Grid item key={plan.id} xs={12} sm={6} md={4}>
                    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', borderRadius: '1rem', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)' }}>
                      <CardContent sx={{ flexGrow: 1, p: 4 }}>
                        {plan.chipLabel && <Chip label={plan.chipLabel} color="primary" sx={{ mb: 2 }} />}
                        <Typography variant="h5" component="h3" sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mb: 2 }}>
                          {plan.title}
                        </Typography>
                        <Typography variant="h4" component="p" sx={{ color: theme.palette.primary.main, mb: 2 }}>
                          {plan.price}
                        </Typography>
                        <Typography variant="body1" sx={{ color: theme.palette.text.secondary, mb: 3 }}>
                          {plan.description}
                        </Typography>
                        <Box>
                          {plan.features.map((feature, index) => (
                            <Typography key={index} variant="body2" sx={{ color: theme.palette.text.secondary, mb: 1 }}>
                              ✓ {feature}
                            </Typography>
                          ))}
                        </Box>
                      </CardContent>
                      <CardActions sx={{ p: 4, pt: 0 }}>
                        <Button
                          fullWidth
                          variant="contained"
                          color="primary"
                          sx={{
                            fontWeight: 'bold',
                            padding: '0.75rem 1.5rem',
                          }}
                          onClick={() => handleSelectPlan(plan.id)}
                        >
                          {plan.buttonText}
                        </Button>
                      </CardActions>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </>
          )}
        </Container>
      </Box>
    </ThemeProvider>
  );
};

export default Pricing;